import React, { useEffect, useRef, useState, useCallback } from 'react';
import client from '../../../services/graphqlClient';
import { useNotification } from '../../../context/NotificationContext';
import './UbicacionProyecto.css';

// Leaflet se carga autoalojado desde public/leaflet (ver public/index.html), así
// que aquí se usa window.L directamente: sin dependencia npm ni react-leaflet.
const L = () => window.L;

// Centro por defecto cuando el proyecto aún no tiene coordenadas: Medellín.
const CENTRO_DEFAULT = [6.2442, -75.5812];
const RADIO_DEFAULT = 1000;

// Acepta lo que la gente pega de Google Maps:
//   "6.2442, -75.5812"  ·  "6.2442 -75.5812"
//   https://www.google.com/maps/place/.../@6.2442,-75.5812,17z/...
//   https://maps.google.com/?q=6.2442,-75.5812
//   ...!3d6.2442!4d-75.5812...
function parsearCoordenadas(texto) {
  const t = (texto || '').trim();
  if (!t) return null;
  const patrones = [
    /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/,
    /[?&]q=(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/,
    /!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/,
    /^(-?\d{1,2}\.\d+)\s*[,; ]\s*(-?\d{1,3}\.\d+)$/,
  ];
  for (const re of patrones) {
    const m = t.match(re);
    if (m) {
      const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }
  }
  return null;
}

export default function UbicacionProyecto({ proyecto, onProyectoActualizado }) {
  const { success, error } = useNotification();

  const mapaRef   = useRef(null);   // instancia de L.map
  const divRef    = useRef(null);   // contenedor del mapa
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  const tieneCoords = proyecto?.latitud != null && proyecto?.longitud != null;

  const [lat, setLat]     = useState(tieneCoords ? Number(proyecto.latitud) : null);
  const [lng, setLng]     = useState(tieneCoords ? Number(proyecto.longitud) : null);
  const [radio, setRadio] = useState(proyecto?.radio_metros || RADIO_DEFAULT);
  const [busqueda, setBusqueda] = useState(proyecto?.ubicacion_detallada || proyecto?.direccion || '');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cambiado =
    (tieneCoords ? Number(proyecto.latitud) : null) !== lat ||
    (tieneCoords ? Number(proyecto.longitud) : null) !== lng ||
    (proyecto?.radio_metros || RADIO_DEFAULT) !== Number(radio);

  // ── Marcador y círculo ────────────────────────────────
  const pintar = useCallback((la, lo, r) => {
    const map = mapaRef.current;
    if (!map || la == null || lo == null) return;
    const Lf = L();
    if (!markerRef.current) {
      markerRef.current = Lf.marker([la, lo], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', (e) => {
        const p = e.target.getLatLng();
        setLat(+p.lat.toFixed(7)); setLng(+p.lng.toFixed(7));
      });
    } else {
      markerRef.current.setLatLng([la, lo]);
    }
    if (!circleRef.current) {
      circleRef.current = Lf.circle([la, lo], {
        radius: r, color: '#2563eb', weight: 1.5, fillColor: '#3b82f6', fillOpacity: 0.12,
      }).addTo(map);
    } else {
      circleRef.current.setLatLng([la, lo]);
      circleRef.current.setRadius(r);
    }
  }, []);

  // ── Crear el mapa una sola vez ────────────────────────
  useEffect(() => {
    const Lf = L();
    if (!Lf || !divRef.current || mapaRef.current) return;

    const map = Lf.map(divRef.current, { zoomControl: true }).setView(
      tieneCoords ? [Number(proyecto.latitud), Number(proyecto.longitud)] : CENTRO_DEFAULT,
      tieneCoords ? 16 : 12
    );
    Lf.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    // Clic en el mapa = mover el puntero ahí.
    map.on('click', (e) => {
      setLat(+e.latlng.lat.toFixed(7));
      setLng(+e.latlng.lng.toFixed(7));
    });

    mapaRef.current = map;
    if (tieneCoords) pintar(Number(proyecto.latitud), Number(proyecto.longitud), proyecto.radio_metros || RADIO_DEFAULT);

    // Si el contenedor cambia de tamaño (pestaña recién visible), Leaflet necesita recalcular.
    setTimeout(() => map.invalidateSize(), 150);

    return () => { map.remove(); mapaRef.current = null; markerRef.current = null; circleRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cada vez que cambian lat/lng/radio, se refleja en el mapa.
  useEffect(() => {
    if (lat != null && lng != null) pintar(lat, lng, Number(radio) || RADIO_DEFAULT);
  }, [lat, lng, radio, pintar]);

  const centrar = (la, lo, zoom = 17) => mapaRef.current?.setView([la, lo], zoom);

  // ── Buscar: coordenadas pegadas o dirección (Nominatim) ──
  const buscar = async (e) => {
    e?.preventDefault?.();
    const coords = parsearCoordenadas(busqueda);
    if (coords) {
      setLat(coords.lat); setLng(coords.lng); centrar(coords.lat, coords.lng);
      setResultados([]);
      return;
    }
    if (!busqueda.trim()) return;
    setBuscando(true);
    setResultados([]);
    try {
      const url = 'https://nominatim.openstreetmap.org/search?format=json&limit=6&countrycodes=co&q='
        + encodeURIComponent(busqueda.trim());
      const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
      const data = await res.json();
      if (!data.length) error('No se encontró esa dirección. Prueba con más detalle o pega las coordenadas.');
      setResultados(data);
    } catch {
      error('No se pudo consultar el buscador de direcciones.');
    } finally {
      setBuscando(false);
    }
  };

  const elegirResultado = (r) => {
    const la = parseFloat(r.lat), lo = parseFloat(r.lon);
    setLat(la); setLng(lo); centrar(la, lo);
    setResultados([]);
  };

  const usarMiUbicacion = () => {
    if (!navigator.geolocation) return error('El navegador no permite obtener tu ubicación.');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = +pos.coords.latitude.toFixed(7), lo = +pos.coords.longitude.toFixed(7);
        setLat(la); setLng(lo); centrar(la, lo);
      },
      () => error('No se pudo obtener tu ubicación desde el navegador.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ── Guardar / quitar ──────────────────────────────────
  const guardar = async (quitar = false) => {
    setGuardando(true);
    try {
      const vars = quitar
        ? { id: proyecto.id, latitud: null, longitud: null, radio_metros: RADIO_DEFAULT }
        : { id: proyecto.id, latitud: lat, longitud: lng, radio_metros: Math.max(50, parseInt(radio, 10) || RADIO_DEFAULT) };
      const d = await client.mutate(
        `mutation($id: Int!, $latitud: Float, $longitud: Float, $radio_metros: Int) {
          actualizarUbicacionProyecto(id: $id, latitud: $latitud, longitud: $longitud, radio_metros: $radio_metros) { success message }
        }`, vars);
      if (d.actualizarUbicacionProyecto.success) {
        success(quitar ? 'Ubicación eliminada' : 'Ubicación guardada');
        if (quitar) {
          setLat(null); setLng(null); setRadio(RADIO_DEFAULT);
          markerRef.current?.remove(); circleRef.current?.remove();
          markerRef.current = null; circleRef.current = null;
        }
        onProyectoActualizado?.();
      } else {
        error(d.actualizarUbicacionProyecto.message || 'No se pudo guardar');
      }
    } catch (err) {
      error(err.message || 'No se pudo guardar la ubicación');
    } finally {
      setGuardando(false);
    }
  };

  const leafletOk = !!L();

  return (
    <div className="ubi">
      <div className="ubi-head">
        <div>
          <h3 className="ubi-title">Ubicación del proyecto</h3>
          <p className="ubi-sub">
            Los ingenieros solo podrán marcar <strong>entrada</strong> desde la app si están a menos del radio
            indicado de este punto. Sin ubicación registrada, la app exige GPS pero no valida la distancia.
          </p>
        </div>
        <span className={`ubi-estado ${tieneCoords ? 'ok' : 'falta'}`}>
          {tieneCoords ? '● Ubicación registrada' : '○ Sin ubicación'}
        </span>
      </div>

      {!leafletOk && (
        <div className="ubi-alerta">
          No se pudo cargar el mapa. Verifica que la carpeta <code>/leaflet</code> esté publicada en el hosting.
        </div>
      )}

      <form className="ubi-buscar" onSubmit={buscar}>
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Pega coordenadas o un link de Google Maps, o escribe la dirección…"
        />
        <button type="submit" className="ubi-btn" disabled={buscando}>{buscando ? 'Buscando…' : 'Buscar'}</button>
        <button type="button" className="ubi-btn ghost" onClick={usarMiUbicacion} title="Usar la ubicación de este computador">📍 Mi ubicación</button>
      </form>

      {resultados.length > 0 && (
        <ul className="ubi-resultados">
          {resultados.map((r) => (
            <li key={r.place_id} onClick={() => elegirResultado(r)}>{r.display_name}</li>
          ))}
        </ul>
      )}

      <div ref={divRef} className="ubi-mapa" />
      <p className="ubi-ayuda">Haz clic en el mapa para poner el puntero, o arrástralo hasta el sitio exacto.</p>

      <div className="ubi-campos">
        <label>
          Latitud
          <input type="number" step="0.0000001" value={lat ?? ''} onChange={(e) => setLat(e.target.value === '' ? null : parseFloat(e.target.value))} />
        </label>
        <label>
          Longitud
          <input type="number" step="0.0000001" value={lng ?? ''} onChange={(e) => setLng(e.target.value === '' ? null : parseFloat(e.target.value))} />
        </label>
        <label>
          Radio permitido (m)
          <input type="number" min="50" step="50" value={radio} onChange={(e) => setRadio(e.target.value)} />
        </label>
      </div>

      <div className="ubi-acciones">
        <button
          className="ubi-btn primary"
          disabled={guardando || lat == null || lng == null || !cambiado}
          onClick={() => guardar(false)}
        >
          {guardando ? 'Guardando…' : 'Guardar ubicación'}
        </button>
        {tieneCoords && (
          <button className="ubi-btn danger" disabled={guardando} onClick={() => guardar(true)}>
            Quitar ubicación
          </button>
        )}
        {lat != null && lng != null && (
          <a
            className="ubi-link"
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank" rel="noreferrer"
          >
            Abrir en Google Maps ↗
          </a>
        )}
      </div>
    </div>
  );
}
